import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { getOgLocale, generateHreflangLinks, getJsonLdLanguage } from '../utils/seoUtils';
import { SITE_ORIGIN, getLocalizedSiteUrl } from '../config/site';
// faqConfig and insidePreviewConfig will be lazy loaded when needed
import { Helmet } from 'react-helmet-async';
// articlesConfig will be lazy loaded when needed
import UnifiedCalculator from './UnifiedCalculator';
import { generateArticleSlug } from '../utils/slugUtils';
import Footer from './Footer';
import SiteHeader from './SiteHeader';
import { articlesConfig as defaultArticlesConfig } from '../config/articlesConfig';
import { useLanguage } from '../contexts/LanguageContext';
import { createLocalizedUrl } from '../utils/languageUtils';
import { setPrerenderReady, setPrerenderReadyOptimized, isPrerender } from '../utils/prerenderUtils';

const EARLY_ACCESS_START_DATE = new Date('2025-03-08');
const TOTAL_EARLY_ACCESS_SPOTS = 200;
const SPOTS_TAKEN_PER_DAY = 1;

// Configuration for top guides to display in carousel - moved outside component to prevent recreation
const TOP_GUIDES_IDS = ['033','032','026','021','023','024','028','020','002', '019','017','007', '022', '014', '015', '001', '016', '003', '004', '013']; 

const InsidePreview = () => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState(null);
    const [insidePreviewConfig, setInsidePreviewConfig] = useState(null);

    useEffect(() => {
        let cancelled = false;
        
        // Lazy load inside preview config
        import('../config/insidePreviewConfig')
            .then((module) => {
                if (!cancelled) {
                    const config = module.insidePreviewConfig;
                    setInsidePreviewConfig(config);
                    setActiveTab(config.tabs[0].id);
                }
            })
            .catch((error) => {
                if (!cancelled) {
                    console.error('Failed to load inside preview config:', error);
                }
            });
        
        return () => {
            cancelled = true;
        };
    }, []);

    if (!insidePreviewConfig) {
        return (
            <section id="features" className="py-4 lg:py-8 scroll-mt-20" aria-label="Features">
                <div className="container mx-auto px-4">
                    <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-5 lg:p-6">
                        <div className="animate-pulse">
                            <div className="h-8 bg-gray-200 rounded w-1/2 mx-auto mb-4"></div>
                            <div className="h-4 bg-gray-200 rounded w-3/4 mx-auto mb-8"></div>
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                <div className="space-y-4">
                                    <div className="h-6 bg-gray-200 rounded w-1/3"></div>
                                    <div className="h-4 bg-gray-200 rounded"></div>
                                    <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                                </div>
                                <div className="h-48 bg-gray-200 rounded"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section id="features" className="py-4 lg:py-8 scroll-mt-20" aria-label="Features">
            <div className="container mx-auto px-4">
                <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-5 lg:p-6">
                    <h2 className="text-3xl lg:text-4xl font-bold text-center mb-3 text-blue-600">
                        {t('welcome.seeWhatsInside')}
                    </h2>
                    <p className="text-lg lg:text-xl text-gray-600 text-center mb-6 lg:mb-8 max-w-3xl mx-auto">
                        {t('welcome.seeWhatsInsideSubtitle')}
                    </p>

                    {/* Tab Navigation - Reduced spacing */}
                    <div className="overflow-x-auto pb-3 mb-6 lg:mb-8">
                        <div className="flex justify-start lg:justify-center gap-4 lg:gap-6 min-w-max mx-auto">
                            {insidePreviewConfig.tabs.map((tab) => (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`flex flex-col items-center p-3 lg:p-4 rounded-xl transition-all min-w-[100px] ${
                                        activeTab === tab.id 
                                            ? 'bg-gray-50 shadow-md scale-105' 
                                            : 'hover:bg-gray-50'
                                    }`}
                                >
                                    <span className={`text-3xl lg:text-4xl mb-2 lg:mb-3 transition-all ${
                                        activeTab === tab.id 
                                            ? 'opacity-100 scale-110' 
                                            : 'opacity-50 grayscale hover:opacity-75'
                                    }`}>
                                        {tab.icon}
                                    </span>
                                    <span className={`text-xs lg:text-sm font-medium text-center ${
                                        activeTab === tab.id 
                                            ? 'text-blue-600' 
                                            : 'text-gray-500'
                                    }`}>
                                        {t(tab.label)}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Tab Content */}
                    {insidePreviewConfig.tabs.map((tab) => (
                        <div
                            key={tab.id}
                            className={`${
                                activeTab === tab.id ? 'block' : 'hidden'
                            }`}
                        >
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
                                {/* Left Content */}
                                <div className="p-6 space-y-6 order-2 lg:order-1">
                                    <h3 className="text-2xl lg:text-3xl font-bold text-gray-900">
                                        {t(tab.title)}
                                    </h3>
                                    <ul className="space-y-4">
                                        {tab.description.map((item, index) => (
                                            <li key={index} className="flex items-start gap-3">
                                                <svg className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                                </svg>
                                                <span className="text-gray-600 text-sm lg:text-base">{t(item)}</span>
                                            </li>
                                        ))}
                                    </ul>
                                    <Link
                                        to={tab.ctaLink}
                                        className="inline-block w-full lg:w-auto px-6 py-3 bg-blue-600 text-white text-center rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                                    >
                                        {t(tab.ctaText)}
                                    </Link>
                                </div>

                                {/* Right Content */}
                                <div className="order-1 lg:order-2">
                                    <img
                                        src={tab.image}
                                        srcSet={tab.imageSrcSet}
                                        sizes={tab.imageSizes}
                                        alt={t(tab.label)}
                                        className="w-full h-auto object-cover rounded-lg"
                                        loading="lazy"
                                    />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

const Welcome = () => {
    const { t, ready } = useTranslation();
    const location = useLocation();
    const { currentLanguage } = useLanguage();
    const [openFaqIndex, setOpenFaqIndex] = useState(null);
    const [currentSlide, setCurrentSlide] = useState(0);
    const [shouldLoadGuideImages, setShouldLoadGuideImages] = useState(false);
    const [articlesData, setArticlesData] = useState(null);
    const [topGuides, setTopGuides] = useState([]);
    const [faqConfig, setFaqConfig] = useState(null);
    
    // Ref to track resize listener cleanup
    const resizeCleanupRef = useRef(null);

    // Helper: Get English slug for an article by ID (keeps URLs stable across locales)
    const getEnglishSlug = (article) => {
        if (!article || !article.id) return '';
        
        // Find the English version of this article by ID
        const allEnArticles = defaultArticlesConfig.categories.flatMap(c => c.articles || []);
        const enArticle = allEnArticles.find(a => a && a.id === article.id);
        
        if (enArticle) {
            return generateArticleSlug(enArticle);
        }
        
        // No fallback - prevent translated slug generation
        console.error(`Missing English article for ID: ${article.id}`);
        return null;
    };

    // Calculate total number of slides
    const totalSlides = Math.ceil(topGuides.length / 3);

    // Clean up article-specific meta tags and hreflang when mounting (prevents persistence from ArticleDetail)
    useEffect(() => {
        const cleanup = () => {
            // Remove article-specific Open Graph meta tags
            const articleMetaSelectors = [
                'meta[property="article:published_time"]',
                'meta[property="article:modified_time"]',
                'meta[property="og:updated_time"]',
                'meta[property="article:author"]',
                'meta[property="article:section"]',
                'meta[property="article:tag"]'
            ];
            
            articleMetaSelectors.forEach(selector => {
                document.querySelectorAll(selector).forEach(el => el.remove());
            });
            
            // Note: hreflang cleanup removed to prevent race conditions with Helmet
            // App-level cleanup handles non-Helmet hreflang links
        };
        
        // Clean immediately
        cleanup();
        
        // Clean again after a short delay to catch late-mounted Helmet tags
        const timeoutId = setTimeout(cleanup, 100);
        
        return () => clearTimeout(timeoutId);
    }, []);


    // Add toggleFaq function
    const toggleFaq = (index) => {
        setOpenFaqIndex(openFaqIndex === index ? null : index);
    };

    // Language change handler


    // Define pricing features using individual translation keys
    const pricingFeatures = [
        t('welcome.pricing.featuresList.interactiveTimeline', '🗺️ Interactive Immigration Timeline'),
        t('welcome.pricing.featuresList.documentChecklist', '📋 Personalized Document Checklist'),
        t('welcome.pricing.featuresList.progressDashboard', '📝 Visual Progress Dashboard'),
        t('welcome.pricing.featuresList.cityExplorer', '🌟 City Cost and Life Explorer'),
        t('welcome.pricing.featuresList.deadlineReminders', '📅 Smart Deadline Reminders'),
        t('welcome.pricing.featuresList.processGuides', '📚 Step-by-Step Process Guides'),
        t('welcome.pricing.featuresList.requirementsLibrary', '🔍 Immigration Requirements Library'),
        t('welcome.pricing.featuresList.insightsUpdates', '📈 Immigration Insights & Updates'),
        t('welcome.pricing.featuresList.serviceDirectory', '📖 Verified Service Provider Directory'),
        t('welcome.pricing.featuresList.aiAssistant', '🤖 AI Immigration & Tax Assistant')
    ];


    const currentFaqEntries = faqConfig?.[currentLanguage] || faqConfig?.en || [];

    useEffect(() => {
        // Reduce the scroll padding even further
        document.documentElement.style.scrollPadding = '48px 0 0 0'; // Reduced from 64px to 48px
        
        return () => {
            document.documentElement.style.scrollPadding = '0';
        };
    }, []);

    // Add intersection observer for lazy loading FAQ config
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting && !faqConfig) {
                        // Lazy load FAQ config when section comes into view
                        import('../config/faqConfig')
                            .then((module) => {
                                setFaqConfig(module.faqConfig);
                            })
                            .catch((error) => {
                                console.error('Failed to load FAQ config:', error);
                            });
                        observer.disconnect(); // Stop observing once loaded
                    }
                });
            },
            {
                rootMargin: '200px' // Start loading 200px before the section comes into view for better mobile performance
            }
        );

        const faqSection = document.querySelector('#faq');
        if (faqSection) {
            observer.observe(faqSection);
        }

        return () => {
            observer.disconnect();
        };
    }, [faqConfig]);

    // Handle video timestamp navigation from URL parameters
    useEffect(() => {
        const handleVideoTimestamp = () => {
            // Parse hash to extract both section and timestamp
            const hash = window.location.hash;

            // Extract timestamp from hash (e.g., #demo?t=22)
            let timestamp = null;
            if (hash.includes('?t=')) {
                const hashParts = hash.split('?t=');
                if (hashParts.length > 1) {
                    timestamp = hashParts[1].split('&')[0]; // Handle multiple params
                }
            }
            
            if (timestamp) {
                //console.log('Timestamp detected:', timestamp);
                
                // Scroll to the demo section first
                const demoSection = document.getElementById('demo');
                if (demoSection) {
                    //console.log('Scrolling to demo section');
                    demoSection.scrollIntoView({ behavior: 'smooth' });
                } else {
                    //console.log('Demo section not found');
                }

                // Then handle video timestamp
                const videoElement = document.getElementById('demo-video');
                if (videoElement) {
                    const timeInSeconds = parseInt(timestamp, 10);
                    //console.log('Setting video time to:', timeInSeconds, 'seconds');
                    
                    if (!isNaN(timeInSeconds)) {
                        const setVideoTime = () => {
                            //console.log('Executing setVideoTime, current video time:', videoElement.currentTime);
                            videoElement.currentTime = timeInSeconds;
                            //console.log('Video time set to:', videoElement.currentTime);
                            
                            // Auto-play the video from the specified timestamp
                            videoElement.play().catch(() => {
                                // Play failed (user interaction required), but timestamp is set
                                console.log('Auto-play prevented, but video time set to', timeInSeconds, 'seconds');
                            });
                        };

                        if (videoElement.readyState >= 2) {
                            // Video metadata is already loaded
                            setTimeout(setVideoTime, 500); // Small delay to ensure smooth scroll completes
                        } else {
                            // Wait for video metadata to load
                            videoElement.addEventListener('loadedmetadata', () => {
                                setTimeout(setVideoTime, 500);
                            }, { once: true });
                        }
                    }
                }
            }
        };

        // Handle initial load with a small delay to ensure DOM is ready
        setTimeout(handleVideoTimestamp, 100);

        // Handle browser back/forward navigation
        const handlePopState = () => {
            setTimeout(handleVideoTimestamp, 200);
        };

        window.addEventListener('popstate', handlePopState);
        
        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, []);

    // Handle general hash navigation (e.g., #demo, #pricing, #features)
    useEffect(() => {
        const handleHashScroll = () => {
            const hash = window.location.hash;
            if (hash) {
                // Extract the section ID (remove # and any query params)
                const sectionId = hash.split('?')[0].substring(1);
                
                if (sectionId) {
                    const element = document.getElementById(sectionId);
                    if (element) {
                        // Small delay to ensure content is fully rendered
                        setTimeout(() => {
                            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }, 100);
                    }
                }
            }
        };

        // Handle initial load with hash
        handleHashScroll();

        // Also handle hash changes during page view
        const handleHashChange = () => {
            handleHashScroll();
        };

        window.addEventListener('hashchange', handleHashChange);
        
        return () => {
            window.removeEventListener('hashchange', handleHashChange);
        };
    }, [location]);

    // Replace the useEffect and lifetimeUsers state with this calculation
    const calculateRemainingSpots = () => {
        const today = new Date();
        const daysSinceLaunch = Math.floor((today - EARLY_ACCESS_START_DATE) / (1000 * 60 * 60 * 24));
        
        // If launch date is in the future, show all spots
        if (daysSinceLaunch < 0) {
            return TOTAL_EARLY_ACCESS_SPOTS;
        }
        
        const estimatedSpotsTaken = Math.min(daysSinceLaunch * SPOTS_TAKEN_PER_DAY, TOTAL_EARLY_ACCESS_SPOTS);
        const remainingSpots = Math.max(TOTAL_EARLY_ACCESS_SPOTS - estimatedSpotsTaken, 0);
        
        // For debugging
        //console.log({
        //    daysSinceLaunch,
        //    estimatedSpotsTaken,
        //    remainingSpots
        //});
        
        return remainingSpots;
    };

    // Use this in your render logic instead of lifetimeUsers
    const remainingSpots = calculateRemainingSpots();

    // Articles data is now lazy loaded when the guides section comes into view

    // Add intersection observer for lazy loading guide images and articles data
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setShouldLoadGuideImages(true);
                        
                        // Lazy load articles data when section comes into view
                        if (!articlesData) {
                            import('../config/configRegistry')
                                .then((module) => {
                                    const storedLanguage = localStorage.getItem('selectedLanguage');
                                    const userLocale = storedLanguage || navigator.language || 'en';
                                    return module.loadArticlesConfigForLocale(userLocale);
                                })
                                .then((articlesConfig) => {
                                    setArticlesData(articlesConfig);
                                    
                                    // Process top guides once data is loaded
                                    const processedTopGuides = articlesConfig.categories[0].articles
                                        .filter(article => TOP_GUIDES_IDS.includes(article.id))
                                        .sort((a, b) => TOP_GUIDES_IDS.indexOf(a.id) - TOP_GUIDES_IDS.indexOf(b.id));
                                    
                                    setTopGuides(processedTopGuides);
                                })
                                .catch((error) => {
                                    console.error('Failed to load articles config:', error);
                                    // Fallback to default config
                                    setArticlesData(defaultArticlesConfig);
                                    
                                    const processedTopGuides = defaultArticlesConfig.categories[0].articles
                                        .filter(article => TOP_GUIDES_IDS.includes(article.id))
                                        .sort((a, b) => TOP_GUIDES_IDS.indexOf(a.id) - TOP_GUIDES_IDS.indexOf(b.id));
                                    
                                    setTopGuides(processedTopGuides);
                                });
                        }
                        
                        observer.disconnect(); // Stop observing once we've triggered loading
                    }
                });
            },
            {
                rootMargin: '200px' // Start loading 200px before the section comes into view for better mobile performance
            }
        );

        const guidesSection = document.querySelector('#top-guides-section');
        if (guidesSection) {
            observer.observe(guidesSection);
        }

        return () => {
            observer.disconnect();
        };
            }, [articlesData]);

    // Cleanup resize listener on component unmount
    useEffect(() => {
        return () => {
            if (resizeCleanupRef.current) {
                resizeCleanupRef.current();
            }
        };
    }, []);


    // Clean up only non-Helmet article meta tags (strays from older builds)
    useEffect(() => {
        const selectors = [
            'meta[property="article:published_time"]:not([data-rh="true"])',
            'meta[property="article:modified_time"]:not([data-rh="true"])',
            'meta[property="og:updated_time"]:not([data-rh="true"])',
            'meta[property="article:author"]:not([data-rh="true"])',
            'meta[property="article:section"]:not([data-rh="true"])',
            'meta[property="article:tag"]:not([data-rh="true"])',
        ];
        selectors.forEach(sel => {
            document.querySelectorAll(sel).forEach(n => n.remove());
        });
    }, []);

    // Set prerender ready when translations are loaded and content is about to render
    useEffect(() => {
        if (ready) {
            // Use optimized function for prerender.io to handle mobile vs desktop differences
            if (isPrerender()) {
                setPrerenderReadyOptimized();
            } else {
                setPrerenderReady();
            }
        }
    }, [ready]);

    // Wait for translations to be ready before rendering
    if (!ready) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
            </div>
        );
    }

    return (
        <div className="bg-white min-h-screen text-gray-900">
            <Helmet 
                prioritizeSeoTags={true} 
                defer={false}
            >
                <html lang={currentLanguage} />
                <title>{t('meta.homeTitle', 'LiveLoveSpain - Your App for Immigration & Life in Spain')}</title>
                <meta name="description" content={t('meta.homeDescription', 'Take control of your move to Spain with our all-in-one app. Access step-by-step guides for visas, residency, and housing. Start your Spanish journey today.')} />
                <meta name="author" content="Gerard B." />
                <meta name="page-component" content="Welcome" data-test="helmet-rendering" />
                
                {/* Canonical URL - Must come early */}
                <link 
                    rel="canonical" 
                    href={getLocalizedSiteUrl('', currentLanguage)} 
                />
                
                {/* Hreflang tags - Critical for international SEO */}
                {generateHreflangLinks(SITE_ORIGIN, '', currentLanguage).map((link, index) => (
                    <link key={`hreflang-${link.hreflang}`} rel={link.rel} href={link.href} hreflang={link.hreflang} />
                ))}
                
                {/* Preconnect to external resources for performance */}
                <link rel="preconnect" href="https://www.googletagmanager.com" crossOrigin="anonymous" />
                <link rel="preconnect" href="https://www.googleadservices.com" crossOrigin="anonymous" />
                
                <style>
                    {`
                    @keyframes gradient-x {
                        0%, 100% {
                            background-size: 200% 200%;
                            background-position: left center;
                        }
                        50% {
                            background-size: 200% 200%;
                            background-position: right center;
                        }
                    }
                    .animate-gradient-x {
                        animation: gradient-x 4s ease infinite;
                    }
                    @keyframes float {
                        0%, 100% { transform: translateY(0px); }
                        50% { transform: translateY(-8px); }
                    }
                    .animate-float {
                        animation: float 6s ease-in-out infinite;
                    }
                    @keyframes pulse-glow {
                        0%, 100% { opacity: 0.2; }
                        50% { opacity: 0.5; }
                    }
                    .animate-pulse-glow {
                        animation: pulse-glow 3s ease-in-out infinite;
                    }
                    `}
                </style>
                
                {/* Robots tag */}
                <meta key="meta-robots-homepage" name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
                <meta key="meta-googlebot-homepage" name="googlebot" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
                
                {/* LCP image preloading with stable keys */}
                <link key="preload-hero-desktop" rel="preload" as="image" href="/assets/Livelovespain_demo.webp" fetchpriority="high" />
                <link key="preload-hero-tablet" rel="preload" as="image" href="/assets/Livelovespain_demo-640.webp" fetchpriority="high" media="(max-width: 1024px)" />
                <link key="preload-hero-mobile-lg" rel="preload" as="image" href="/assets/Livelovespain_demo-480.webp" fetchpriority="high" media="(max-width: 768px)" />
                <link key="preload-hero-mobile-sm" rel="preload" as="image" href="/assets/Livelovespain_demo-320.webp" fetchpriority="high" media="(max-width: 480px)" />
                
                {/* Open Graph tags */}
                <meta key="og-title-homepage" property="og:title" content={t('meta.homeTitle')} />
                <meta key="og-description-homepage" property="og:description" content={t('meta.homeDescription')} />
                <meta key="og-type-homepage" property="og:type" content="website" />
                <meta key="og-url-homepage" property="og:url" content={getLocalizedSiteUrl('', currentLanguage)} />
                <meta key="og-image-homepage" property="og:image" content={`${SITE_ORIGIN}/assets/social-preview-1200x630.png`} />
                <meta key="og-image-secure-homepage" property="og:image:secure_url" content={`${SITE_ORIGIN}/assets/social-preview-1200x630.png`} />
                <meta key="og-image-width-homepage" property="og:image:width" content="1200" />
                <meta key="og-image-height-homepage" property="og:image:height" content="630" />
                <meta key="og-image-alt-homepage" property="og:image:alt" content={t('meta.homeImageAlt')} />
                
                {/* Twitter Card tags */}
                <meta key="twitter-card-homepage" name="twitter:card" content="summary_large_image" />
                <meta key="twitter-title-homepage" name="twitter:title" content={t('meta.homeTitle')} />
                <meta key="twitter-description-homepage" name="twitter:description" content={t('meta.homeDescription')} />
                <meta key="twitter-image-homepage" name="twitter:image" content={`${SITE_ORIGIN}/assets/social-preview-1200x630.png`} />
                <meta key="twitter-image-alt-homepage" name="twitter:image:alt" content={t('meta.homeImageAlt')} />
                <meta key="og-image-type-homepage" property="og:image:type" content="image/png" />
                <meta key="og-site-name-homepage" property="og:site_name" content={t('meta.appName')} />
                <meta key="og-locale-homepage" property="og:locale" content={getOgLocale(currentLanguage)} />
                {(() => {
                    const currentOgLocale = getOgLocale(currentLanguage);
                    const allLocales = ['en_US', 'es_ES', 'fr_FR', 'de_DE', 'pt_PT'];
                    const alternateLocales = allLocales.filter(locale => locale !== currentOgLocale);
                    return alternateLocales.map(locale => (
                        <meta key={`og-locale-alt-${locale}`} property="og:locale:alternate" content={locale} />
                    ));
                })()}
                
                {/* Device-specific image preloading */}
                
                {/* Preconnect to external resources - Eliminate resource load delay */}
                {/* Removed duplicate preconnect - already in index.html */}
                
                {/* Note: Hero image preloads moved to server-rendered HTML for better LCP */}
                {/* Remove these from Helmet as they're ineffective when loaded after React mounts */}

                {/* Consolidated Page Schema - Single @graph with all page entities */}
                <script 
                    id="ld-welcome"
                    type="application/ld+json" 
                    data-page={getLocalizedSiteUrl('', currentLanguage)}
                    data-component="welcome"
                >
                    {(() => {
                        const schema = {
                        "@context": "https://schema.org",
                        "@graph": [
                            {
                        "@type": "SoftwareApplication",
                        "name": t('meta.appName'),
                        "description": t('meta.homeDescription'),
                        "url": SITE_ORIGIN,
                        "applicationCategory": "EducationalApplication",
                        "mainEntityOfPage": {
                            "@type": "WebPage",
                            "@id": SITE_ORIGIN
                        },
                        "offers": {
                            "@type": "Offer",
                            "price": "0",
                            "priceCurrency": "EUR"
                        },
                        "featureList": [
                            t('welcome.pricing.featuresList.interactiveTimeline'),
                            t('welcome.pricing.featuresList.documentChecklist'),
                            t('welcome.pricing.featuresList.progressDashboard'),
                            t('welcome.pricing.featuresList.cityExplorer'),
                            t('welcome.pricing.featuresList.deadlineReminders'),
                            t('welcome.pricing.featuresList.processGuides'),
                            t('welcome.pricing.featuresList.requirementsLibrary'),
                            t('welcome.pricing.featuresList.insightsUpdates'),
                            t('welcome.pricing.featuresList.serviceDirectory'),
                            t('welcome.pricing.featuresList.aiAssistant')
                        ],
                        "screenshot": [
                            {
                                "@type": "ImageObject",
                                "url": `${SITE_ORIGIN}/assets/hero-preview.webp`,
                                "caption": "LiveLoveSpain TaskManager"
                            }
                        ],
                        "provider": {
                        "@type": "Organization",
                        "@id": `${SITE_ORIGIN}/#organization`
                    }
                            },
                            {
                        "@type": "WebPage",
                        "@id": `${getLocalizedSiteUrl('', currentLanguage)}#webpage`,
                        "url": getLocalizedSiteUrl('', currentLanguage),
                        "name": t('meta.homeTitle'),
                        "inLanguage": getJsonLdLanguage(currentLanguage),
                        "isPartOf": { "@id": `${SITE_ORIGIN}/#website` },
                        "about": { "@id": `${SITE_ORIGIN}/#organization` },
                        "primaryImageOfPage": {
                            "@type": "ImageObject",
                            "url": `${SITE_ORIGIN}/assets/social-preview-1200x630.png`,
                            "width": 1200,
                            "height": 630
                        }
                            },
                            {
                        "@type": "VideoObject",
                        "name": t('meta.video.heroPreview.name'),
                        "description": t('meta.video.heroPreview.description'),
                        "thumbnailUrl": [
                            `${SITE_ORIGIN}/assets/hero-preview.webp`,
                            `${SITE_ORIGIN}/assets/hero-preview-mobile.webp`
                        ],
                        "uploadDate": "2024-12-01T00:00:00Z",
                        "duration": "PT40S",
                        "contentUrl": "https://fjuwnjwmrayamlgy.public.blob.vercel-storage.com/Livelovespain-Trailer-J6uVuCcerLP9d6NZUT4GVP7Pt1cHv4.mp4",
                        "embedUrl": `${SITE_ORIGIN}/#hero`,
                        "url": `${SITE_ORIGIN}/#hero`,
                        "publisher": {
                            "@type": "Organization",
                            "@id": `${SITE_ORIGIN}/#organization`
                        },
                        "author": {
                            "@type": "Organization",
                            "@id": `${SITE_ORIGIN}/#organization`
                        },
                        "mainEntity": {
                            "@type": "SoftwareApplication",
                            "name": t('meta.appName'),
                            "applicationCategory": "EducationalApplication",
                            "operatingSystem": "Web"
                        },
                        "keywords": t('meta.keywords.heroVideo'),
                        "category": "Education",
                        "isFamilyFriendly": true,
                        "inLanguage": getJsonLdLanguage(currentLanguage),
                        "license": `${SITE_ORIGIN}/terms`,
                        "copyrightHolder": {
                            "@type": "Organization",
                            "@id": `${SITE_ORIGIN}/#organization`
                        },
                        "interactionStatistic": {
                            "@type": "InteractionCounter",
                            "interactionType": "http://schema.org/WatchAction",
                            "userInteractionCount": 0
                                },
                                "potentialAction": {
                                    "@type": "WatchAction",
                                    "target": `${SITE_ORIGIN}/#hero`
                        }
                            },

                            {
                        "@type": "VideoObject",
                        "name": t('meta.video.demoWalkthrough.name'),
                        "description": t('meta.video.demoWalkthrough.description'),
                        "thumbnailUrl": [
                            `${SITE_ORIGIN}/assets/Livelovespain_demo.webp`
                        ],
                        "uploadDate": "2025-06-18T00:00:00Z",
                        "duration": "PT1M20S",
                        "contentUrl": "https://fjuwnjwmrayamlgy.public.blob.vercel-storage.com/LLS_demo-8PqGbNUrpC9R3bEzLellG7H6DHfAJC.mp4",
                        "embedUrl": `${SITE_ORIGIN}/#demo`,
                        "url": `${SITE_ORIGIN}/#demo`,
                        "publisher": {
                            "@type": "Organization",
                            "@id": `${SITE_ORIGIN}/#organization`
                        },
                        "author": {
                            "@type": "Organization",
                            "@id": `${SITE_ORIGIN}/#organization`
                        },
                        "mainEntity": {
                            "@type": "SoftwareApplication",
                            "name": t('meta.appName'),
                            "applicationCategory": "EducationalApplication",
                            "operatingSystem": "Web"
                        },
                        "keywords": t('meta.keywords.demoVideo'),
                        "category": "Education",
                        "isFamilyFriendly": true,
                        "inLanguage": getJsonLdLanguage(currentLanguage),
                        "license": `${SITE_ORIGIN}/terms`,
                        "copyrightHolder": {
                            "@type": "Organization",
                            "@id": `${SITE_ORIGIN}/#organization`
                        },
                        "hasPart": [
                            {
                                "@type": "Clip",
                                "name": t('meta.video.clips.taskManager.name'),
                                "description": t('meta.video.clips.taskManager.description'),
                                "startOffset": 0,
                                "endOffset": 22,
                                "url": `${SITE_ORIGIN}/#demo?t=0`
                            },
                            {
                                "@type": "Clip",
                                "name": t('meta.video.clips.articles.name'),
                                "description": t('meta.video.clips.articles.description'),
                                "startOffset": 22,
                                "endOffset": 35,
                                "url": `${SITE_ORIGIN}/#demo?t=22`
                            },
                            {
                                "@type": "Clip",
                                "name": t('meta.video.clips.directory.name'),
                                "description": t('meta.video.clips.directory.description'),
                                "startOffset": 35,
                                "endOffset": 45,
                                "url": `${SITE_ORIGIN}/#demo?t=35`
                            },
                            {
                                "@type": "Clip",
                                "name": t('meta.video.clips.aiAssistant.name'),
                                "description": t('meta.video.clips.aiAssistant.description'),
                                "startOffset": 45,
                                "endOffset": 58,
                                "url": `${SITE_ORIGIN}/#demo?t=45`
                            },
                            {
                                "@type": "Clip",
                                "name": t('meta.video.clips.costExplorer.name'),
                                "description": t('meta.video.clips.costExplorer.description'),
                                "startOffset": 58,
                                "endOffset": 74,
                                "url": `${SITE_ORIGIN}/#demo?t=58`
                            },
                            {
                                "@type": "Clip",
                                "name": t('meta.video.clips.cityComparison.name'),
                                "description": t('meta.video.clips.cityComparison.description'),
                                "startOffset": 74,
                                "endOffset": 80,
                                "url": `${SITE_ORIGIN}/#demo?t=74`
                            }
                        ],
                        "interactionStatistic": {
                            "@type": "InteractionCounter",
                            "interactionType": "http://schema.org/WatchAction",
                            "userInteractionCount": 0
                                },
                                "potentialAction": {
                                    "@type": "WatchAction",
                                    "target": `${SITE_ORIGIN}/#demo`
                                }
                            },
                            ...(currentFaqEntries.length > 0 ? [{
                            "@type": "FAQPage",
                            "mainEntity": currentFaqEntries.map(faq => ({
                                "@type": "Question",
                                "name": faq.question,
                                "acceptedAnswer": {
                                    "@type": "Answer",
                                    "text": faq.answer
                                }
                            }))
                            }] : []),
                            {
                        "@type": "BreadcrumbList",
                        "itemListElement": [
                            {
                                "@type": "ListItem",
                                "position": 1,
                                "name": t('meta.breadcrumb.home'),
                                "item": SITE_ORIGIN
                                    }
                                ]
                            }
                        ]
                    };
                    return JSON.stringify(schema);
                    })()}
                </script>

            </Helmet>

            {/* Header Section */}
            <SiteHeader />

            {/* Hero Section - Two Column Layout */}
            <section 
                className="relative overflow-hidden bg-gradient-to-b from-white via-blue-50/30 to-green-50/20"
                aria-label="Hero"
            >
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-5 -z-10">
                    <div className="absolute inset-0" style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%234F46E5' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                        backgroundSize: '30px 30px'
                    }}></div>
                </div>

                <div className="relative container mx-auto px-4
                    pt-8 pb-10                   /* Mobile spacing */
                    sm:pt-8 sm:pb-8            /* Tablet spacing */
                    lg:pt-10 lg:pb-10           /* Desktop spacing */
                    scroll-mt-20"
                style={{ scrollPaddingTop: '5rem' }}
            >
                {/* Mobile: Single Column Layout */}
                <div className="lg:hidden flex flex-col items-center gap-4">
                    {/* Trust Badge */}
                <div className="relative group">
                    <div className="relative flex items-center gap-2 bg-gradient-to-r from-blue-50 to-white text-blue-700 px-4 py-2 rounded-lg w-fit animate-fade-in shadow-md border border-blue-100/50">
                        <span className="flex h-2 w-2 relative">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-lg bg-green-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                        </span>
                        <span className="text-xs font-semibold">{t('hero.trust')}</span>
                    </div>
                    </div>

                    {/* Main Heading */}
                    <div className="text-center">
                        <h1 className="text-2xl md:text-3xl font-extrabold leading-tight text-gray-900 py-1" aria-label="Move to Spain Smarter, Cheaper, On Your Terms">
                        <span className="block inline-flex items-center gap-2 flex-wrap justify-center mb-1">
                            <span className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 bg-clip-text text-transparent">
                            {t('welcome.hero.mainTitle')}
                            </span>
                            <img 
                                src="/assets/spain_flag.webp" 
                                alt="Spanish Flag"
                                    className="h-8 sm:h-10 w-auto inline-block drop-shadow-md transform hover:scale-110 transition-transform duration-300"
                                style={{ verticalAlign: 'middle' }}
                            />
                        </span>
                        <span className="block mb-1 bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 bg-clip-text text-transparent">
                            {t('welcome.hero.tagline')}
                        </span>
                        <span className="block bg-gradient-to-r from-blue-600 via-blue-500 to-green-500 bg-clip-text text-transparent animate-gradient-x">
                            {t('welcome.hero.tagline2')}
                        </span>
                    </h1>
                </div>

                    {/* Subheading */}
                <div className="text-center max-w-3xl mx-4 md:mx-0 py-4">
                        <p className="text-base text-gray-600 font-medium leading-relaxed">
                        {t('welcome.hero.description')} 
                        <br className="hidden md:block" />
                        <span className="text-blue-700 font-bold bg-blue-50 px-1 py-1 rounded">{t('welcome.hero.subDescription')}</span>
                    </p>
                </div>

                    {/* Advantages Box */}
                    <div className="w-full max-w-4xl">
                        <div className="relative">
                            <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-300 to-green-300 rounded-xl blur opacity-15"></div>
                            
                            <div className="relative bg-white rounded-xl p-4 md:p-6 shadow-lg border border-gray-100">
                                <h2 className="text-center text-lg md:text-xl font-bold text-gray-800 mb-3 md:mb-5">
                                    Why Choose LiveLoveSpain
                                </h2>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-4">
                                    {[
                                        {
                                            icon: "🎯",
                                            title: t('welcome.hero.features.taskManager'),
                                            subtitle: t('welcome.hero.features.taskManagerSub')
                                        },
                                        {
                                            icon: "🔧",
                                            title: t('welcome.hero.features.allInOne'), 
                                            subtitle: t('welcome.hero.features.allInOneSub')
                                        },
                                        {
                                            icon: "⚡",
                                            title: t('welcome.hero.features.fullControl'),
                                            subtitle: t('welcome.hero.features.fullControlSub')
                                        },
                                        {
                                            icon: "💎",
                                            title: t('welcome.hero.features.transparent'),
                                            subtitle: t('welcome.hero.features.transparentSub')
                                        }
                                    ].map((advantage, index) => (
                                        <div key={index} className="flex items-center gap-2 md:gap-3 group hover:bg-blue-50/30 p-2 md:p-3 rounded-lg transition-all duration-200">
                                            <div className="text-xl md:text-2xl group-hover:scale-110 transition-transform duration-200 flex-shrink-0">
                                                {advantage.icon}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="font-semibold text-gray-800 text-sm md:text-base leading-tight">
                                                    {advantage.title}
                                                </div>
                                                <div className="text-xs md:text-sm text-gray-600 leading-tight">
                                                    {advantage.subtitle}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Calculator */}
                    <div className="w-full py-4 max-w-4xl">
                        <div className="text-center mb-4">
                            <h2 className="text-xl font-bold text-gray-800 mb-1">
                                See Your Potential Savings
                            </h2>
                            <p className="text-sm text-gray-600 max-w-2xl mx-auto">
                                Most residents save <span className="font-bold text-blue-600 bg-blue-50 px-2 py-1 rounded">€500-2,000+</span> by handling Spanish bureaucracy themselves with our guidance
                            </p>
                        </div>
                        <UnifiedCalculator isHero={true} />
                    </div>
                </div>

                {/* Desktop: Two Column Layout */}
                <div className="hidden lg:grid lg:grid-cols-2 lg:gap-10 lg:items-start">
                    {/* Left Column - Text Content */}
                    <div className="space-y-6">
                        {/* Trust Badge */}
                        <div className="relative group">
                            <div className="relative flex items-center gap-2 bg-gradient-to-r from-blue-50 to-white text-blue-700 px-4 py-2 rounded-full w-fit animate-fade-in shadow-md border border-blue-100/50">
                                <span className="flex h-2 w-2 relative">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                                </span>
                                <span className="text-xs font-semibold">{t('hero.trust')}</span>
                            </div>
                        </div>

                        {/* Main Heading */}
                        <div className="space-y-4">
                            <h1 className="text-4xl xl:text-4xl xl:leading-snug 2xl:text-4xl font-extrabold leading-snug text-gray-900 py-2" aria-label="Move to Spain Smarter, Cheaper, On Your Terms" itemProp="headline">
                                <span className="block inline-flex items-center gap-3 mb-2">
                                    <span className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 bg-clip-text text-transparent">
                                    {t('welcome.hero.mainTitle')}
                                    </span>
                                    <img 
                                        src="/assets/spain_flag.webp" 
                                        alt="Spanish Flag"
                                        className="h-12 xl:h-14 w-auto inline-block drop-shadow-md transform hover:scale-110 transition-transform duration-300"
                                        style={{ verticalAlign: 'middle' }}
                                        itemProp="image"
                                    />
                                </span>
                                <span className="block mb-2 bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 bg-clip-text text-transparent">
                                    {t('welcome.hero.tagline')}
                                </span>
                                <span className="block bg-gradient-to-r from-blue-600 via-blue-500 to-green-500 bg-clip-text text-transparent animate-gradient-x">
                                    {t('welcome.hero.tagline2')}
                                </span>
                            </h1>
                        </div>

                        {/* Subheading */}
                        <div className="space-y-4">
                            <p className="text-xl text-gray-600 font-medium leading-relaxed" itemProp="description">
                                {t('welcome.hero.description')} 
                                <br />
                                <span className="text-blue-700 font-bold bg-blue-50 px-1 py-1 rounded">{t('welcome.hero.subDescription')}</span>
                            </p>
                            
                            {/* Conversion-focused stats */}
                            <div className="flex items-center gap-6 text-sm text-gray-600">
                                <div className="flex items-center gap-1">
                                    <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                    <span className="font-semibold">{t('welcome.hero.savings')}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                    <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                    <span className="font-semibold">{t('welcome.hero.trial')}</span>
                                </div>
                            </div>
                        </div>

                        {/* Advantages Grid */}
                        <div className="grid grid-cols-1 gap-2" itemProp="offers" itemScope itemType="https://schema.org/Offer">
                                {[
                                    {
                                        icon: "🎯",
                                        title: t('welcome.hero.features.taskManager'),
                                        subtitle: t('welcome.hero.features.taskManagerSub')
                                    },
                                    {
                                        icon: "🔧",
                                        title: t('welcome.hero.features.allInOne'), 
                                        subtitle: t('welcome.hero.features.allInOneSub')
                                    },
                                    {
                                        icon: "⚡",
                                        title: t('welcome.hero.features.fullControl'),
                                        subtitle: t('welcome.hero.features.fullControlSub')
                                    },
                                    {
                                        icon: "💎",
                                        title: t('welcome.hero.features.transparent'),
                                        subtitle: t('welcome.hero.features.transparentSub')
                                    }
                        ].map((advantage, index) => (
                                <div key={index} className="flex items-center gap-3 group hover:bg-blue-50/30 p-2 rounded-lg transition-all duration-200" itemProp="itemOffered" itemScope itemType="https://schema.org/Service">
                                    <div className="text-2xl group-hover:scale-110 transition-transform duration-200 flex-shrink-0">
                                            {advantage.icon}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                        <div className="font-semibold text-gray-800 text-base leading-tight" itemProp="name">
                                                {advantage.title}
                                            </div>
                                        <div className="text-sm text-gray-600 leading-tight" itemProp="description">
                                                {advantage.subtitle}
                                            </div>
                                        </div>
                            </div>
                        ))}
                    </div>
                    </div>

                    {/* Right Column - Compact Calculator */}
                    <div className="relative" itemScope itemType="https://schema.org/WebApplication">
                        <div className="sticky top-8">
                            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
                                {/* Calculator Header */}
                                <div className="bg-gradient-to-r from-blue-600 to-green-600 px-4 py-3 text-white">
                                    <h2 className="text-lg font-bold mb-1 flex items-center gap-2" itemProp="name">
                                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                            <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
                                        </svg>
                                        {t('unifiedCalculator.header.title', 'Calculate Your Savings')}
                        </h2>
                                    <p className="text-blue-100 text-sm" itemProp="description">
                                        {t('unifiedCalculator.header.subtitle', 'See how much money you can save with LiveLoveSpain vs. hiring professionals')}
                                    </p>
                                    
                                    {/* Urgency indicator */}
                                    <div className="mt-2 flex items-center gap-2 text-xs text-blue-200">
                                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                        </svg>
                                        <span>{t('unifiedCalculator.results.averageSaves', 'Average user saves €1,200+')}</span>
                    </div>
                </div>

                                {/* Compact Calculator Content */}
                                <div className="p-4">
                                    <UnifiedCalculator isHero={true} isCompact={true} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                </div>
            </section>



            {/* Personas Section - Grid Layout */}
            <section id="personas" className="py-4 lg:py-12 bg-gray-50" aria-label="About">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-8 lg:mb-12">
                        <h2 className="text-2xl lg:text-3xl font-bold text-blue-600 mb-4">
                        {t('welcome.noMatterSituation')}
                        </h2>
                        <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                        {t('welcome.personasSubtitle')}
                        </p>
                    </div>

                    {/* Personas Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto mb-12">
                        {[
                            {
                                icon: "👋",
                                title: t('welcome.personas.firstTimeMover', 'First-Time Mover'),
                                headline: t('welcome.personas.newToSpain', 'New to Spain?'),
                                description: t('welcome.personas.firstTimeDescription', 'Get step-by-step guidance for your first move to Spain, from visa applications to finding your first home.')
                            },
                            {
                                icon: "💻",
                                title: t('welcome.personas.digitalNomad', 'Digital Nomad'),
                                headline: t('welcome.personas.workRemotely', 'Work Remotely'),
                                description: t('welcome.personas.digitalNomadDescription', 'Navigate Spanish bureaucracy while maintaining your remote work lifestyle with our specialized guides.')
                            },
                            {
                                icon: "👨‍👩‍👧‍👦",
                                title: t('welcome.personas.employee', 'Employee'),
                                headline: t('welcome.personas.movingWithFamily', 'Moving with Family'),
                                description: t('welcome.personas.employeeDescription', 'Complete family relocation support including school enrollment, healthcare, and family visa processes.')
                            },
                            {
                                icon: "🌴",
                                title: t('welcome.personas.retiree', 'Retiree'),
                                headline: t('welcome.personas.retireInSpain', 'Retire in Spain'),
                                description: t('welcome.personas.retireeDescription', 'Enjoy your golden years in Spain with guidance on retirement visas, healthcare, and pension transfers.')
                            },
                            {
                                icon: "📚",
                                title: t('welcome.personas.student', 'Student'),
                                headline: t('welcome.personas.studyInSpain', 'Study in Spain'),
                                description: t('welcome.personas.studentDescription', 'Navigate student visas, university applications, and student life in Spain with our comprehensive guides.')
                            },
                            {
                                icon: "💼",
                                title: t('welcome.personas.entrepreneur', 'Entrepreneur'),
                                headline: t('welcome.personas.startABusiness', 'Start a Business'),
                                description: t('welcome.personas.entrepreneurDescription', 'Launch your business in Spain with guidance on company formation, tax obligations, and business permits.')
                            },
                            {
                                icon: "📋",
                                title: t('welcome.personas.currentResident', 'Current Resident'),
                                headline: t('welcome.personas.alreadyHere', 'Already Here?'),
                                description: t('welcome.personas.currentResidentDescription', 'Optimize your life in Spain with guides on residency renewals, tax optimization, and local services.')
                            },
                            {
                                icon: "💰",
                                title: t('welcome.personas.investor', 'Investor'),
                                headline: t('welcome.personas.investInSpain', 'Invest in Spain'),
                                description: t('welcome.personas.investorDescription', 'Navigate investment visas, property purchases, and investment opportunities in the Spanish market.')
                            },
                            {
                                icon: "🏥",
                                title: t('welcome.personas.healthcareSeeker', 'Healthcare Seeker'),
                                headline: t('welcome.personas.medicalCare', 'Medical Care'),
                                description: t('welcome.personas.healthcareSeekerDescription', 'Access Spain\'s healthcare system with guides on insurance, medical appointments, and healthcare rights.')
                            }
                        ].map((persona, index) => (
                            <div 
                                key={index}
                                className="group relative bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300"
                            >
                                {/* Static Content */}
                                <div className="p-6 relative z-10 bg-white rounded-t-xl">
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center text-2xl">
                                            {persona.icon}
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-gray-900">{persona.title}</h3>
                                            <p className="text-blue-600 text-sm font-medium">{persona.headline}</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Hover Content */}
                                <div className="absolute inset-x-0 top-[calc(100%-1px)] z-20 opacity-0 invisible 
                                              group-hover:opacity-100 group-hover:visible 
                                              transition-all duration-300 bg-white rounded-b-xl shadow-lg
                                              group-hover:translate-y-0 translate-y-[-10px]
                                              border-t border-gray-100">
                                    <div className="p-6">
                                        <p className="text-sm text-gray-600 mb-4">{persona.description}</p>
                                        <Link 
                                            to="/login"
                                            className="inline-flex items-center justify-center w-full px-4 py-2 bg-yellow-400 text-gray-900 text-sm font-semibold rounded-lg hover:bg-yellow-500 transition-colors"
                                        >
                                            Get Started
                                            <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                            </svg>
                                        </Link>
                                    </div>
                                </div>

                                {/* Hover state shadow overlay */}
                                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 
                                              transition-opacity duration-300 rounded-xl shadow-xl -z-10"></div>
                            </div>
                        ))}
                    </div>

                    {/* Bottom CTA */}
                    <div className="text-center">
                        <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-600 px-4 py-2 rounded-full mb-6">
                            <span className="animate-pulse">💡</span>
                            <span className="text-sm font-medium">{t('welcome.notSureWhereToStart')}</span>
                        </div>
                        <div>
                            <Link 
                                to="/login"
                                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all transform hover:-translate-y-0.5"
                            >
                                {t('welcome.takeTheWizard')}
                                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                </svg>
                            </Link>
                            <p className="text-sm text-gray-600 mt-4">
                                {t('welcome.personalizedPlanDescription')}
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Video Demo Section */}
            <section id="demo" className="py-8 lg:py-16 bg-white scroll-mt-20">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-8 lg:mb-12">
                        <h2 className="text-3xl lg:text-4xl font-bold text-center mb-3 text-blue-600">
                            {t('welcome.seeInAction')}
                        </h2>
                        <p className="text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
                            {t('welcome.watchDemo')}
                        </p>
                    </div>
                    
                    <div className="max-w-5xl mx-auto">
                        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-5 lg:p-6">
                            <div className="aspect-video relative overflow-hidden rounded-xl bg-gray-900">
                                <video 
                                    id="demo-video"
                                    className="w-full h-full object-contain transition-opacity duration-700 ease-in-out cursor-pointer"
                                    controls
                                    controlsList="nodownload"
                                    poster={undefined}
                                    preload="none"
                                    ref={(videoElement) => {
                                        if (videoElement && typeof window !== 'undefined') {
                                            // Set responsive poster based on screen size
                                            const setResponsivePoster = () => {
                                                const width = window.innerWidth;
                                                let posterSrc;
                                                
                                                if (width <= 480) {
                                                    posterSrc = '/assets/Livelovespain_demo-320.webp';
                                                } else if (width <= 768) {
                                                    posterSrc = '/assets/Livelovespain_demo-480.webp';
                                                } else if (width <= 1024) {
                                                    posterSrc = '/assets/Livelovespain_demo-640.webp';
                                                } else {
                                                    posterSrc = '/assets/Livelovespain_demo.webp';
                                                }
                                                
                                                videoElement.poster = posterSrc;
                                            };
                                            
                                            setResponsivePoster();
                                            window.addEventListener('resize', setResponsivePoster);
                                            
                                            // Store cleanup function for component unmount
                                            resizeCleanupRef.current = () => {
                                                window.removeEventListener('resize', setResponsivePoster);
                                            };
                                        }
                                    }}
                                    onClick={(e) => {
                                        // Only handle clicks on the video itself, not on controls
                                        const rect = e.target.getBoundingClientRect();
                                        const controlsHeight = 40; // Approximate height of video controls
                                        const clickY = e.clientY - rect.top;
                                        
                                        // If click is in the controls area (bottom 40px), let native controls handle it
                                        if (clickY > rect.height - controlsHeight) {
                                            return;
                                        }
                                        
                                        // Prevent default to avoid conflicts with native controls
                                        e.preventDefault();
                                        e.stopPropagation();
                                        
                                        // Toggle play/pause on video click
                                        if (e.target.paused) {
                                            e.target.play();
                                        } else {
                                            e.target.pause();
                                        }
                                    }}
                                    onTouchEnd={(e) => {
                                        // Only handle touches on the video itself, not on controls
                                        const rect = e.target.getBoundingClientRect();
                                        const controlsHeight = 40; // Approximate height of video controls
                                        const touch = e.changedTouches[0];
                                        const touchY = touch.clientY - rect.top;
                                        
                                        // If touch is in the controls area (bottom 40px), let native controls handle it
                                        if (touchY > rect.height - controlsHeight) {
                                            return;
                                        }
                                        
                                        // Handle touch events on mobile for play/pause
                                        e.preventDefault();
                                        e.stopPropagation();
                                        
                                        if (e.target.paused) {
                                            e.target.play();
                                        } else {
                                            e.target.pause();
                                        }
                                    }}
                                    onCanPlay={(e) => {
                                        e.target.style.opacity = '1';
                                    }}
                                    onLoadStart={(e) => {
                                        // Ensure video is visible when loading starts
                                        e.target.style.opacity = '1';
                                    }}
                                    onPlay={(e) => {
                                        const playButton = e.target.parentElement.querySelector('.play-button-overlay');
                                        if (playButton) {
                                            playButton.style.opacity = '0';
                                            playButton.style.pointerEvents = 'none';
                                        }
                                    }}
                                    onPause={(e) => {
                                        // Show play button when video is paused
                                        const playButton = e.target.parentNode.querySelector('.play-button-overlay');
                                        if (playButton) {
                                            playButton.style.opacity = '1';
                                            playButton.style.pointerEvents = 'auto';
                                        }
                                    }}
                                    onEnded={(e) => {
                                        const playButton = e.target.parentElement.querySelector('.play-button-overlay');
                                        if (playButton) {
                                            playButton.style.opacity = '1';
                                            playButton.style.pointerEvents = 'auto';
                                        }
                                    }}
                                    onError={(e) => {
                                        const fallbackImg = e.target.parentElement.querySelector('.video-fallback');
                                        if (fallbackImg) {
                                            fallbackImg.style.display = 'block';
                                        }
                                        e.target.style.display = 'none';
                                    }}
                                >
                                    <source 
                                        src="https://fjuwnjwmrayamlgy.public.blob.vercel-storage.com/LLS_demo-8PqGbNUrpC9R3bEzLellG7H6DHfAJC.mp4" 
                                        type="video/mp4"
                                    />
                                    Your browser doesn't support HTML video. 
                                    <a href="https://fjuwnjwmrayamlgy.public.blob.vercel-storage.com/LLS_demo-8PqGbNUrpC9R3bEzLellG7H6DHfAJC.mp4" className="text-blue-600 underline">
                                        Download the video
                                    </a>
                                </video>
                                
                                {/* Fallback image - Responsive */}
                                <picture className="video-fallback w-full h-full hidden">
                                    <source 
                                        media="(max-width: 480px)"
                                        srcSet="/assets/Livelovespain_demo-320.webp"
                                    />
                                    <source 
                                        media="(max-width: 768px)"
                                        srcSet="/assets/Livelovespain_demo-480.webp"
                                    />
                                    <source 
                                        media="(max-width: 1024px)"
                                        srcSet="/assets/Livelovespain_demo-640.webp"
                                    />
                                    <img 
                                        src="/assets/Livelovespain_demo.webp"
                                        alt="LiveLoveSpain Demo Preview"
                                        className="w-full h-full object-contain"
                                    />
                                </picture>
                                
                                {/* Play button overlay for better UX */}
                                <div 
                                    className="play-button-overlay absolute inset-0 flex items-center justify-center cursor-pointer transition-opacity duration-300"
                                    onClick={() => {
                                        const video = document.getElementById('demo-video');
                                        if (video) {
                                            video.play();
                                        }
                                    }}
                                >
                                    <div className="w-20 h-20 lg:w-24 lg:h-24 bg-white/90 rounded-full flex items-center justify-center shadow-lg opacity-80 hover:opacity-100 transition-all duration-200 hover:scale-105">
                                        <svg className="w-8 h-8 text-blue-600 ml-1" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M8 5v14l11-7z"/>
                                        </svg>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Video description */}
                            <div className="mt-6 text-center">
                                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                                    {t('welcome.platformDemo')}
                                </h3>
                                <p className="text-gray-600">
                                    {t('welcome.discoverFeatures')}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Inside Preview Section - Moved below personas */}
            <section className="py-4 lg:py-12">
                <InsidePreview />
            </section>

            {/* Pricing Section - Adjusted padding */}
            <section id="pricing" className="py-8 lg:py-12 bg-white scroll-mt-20">
                <div className="container mx-auto px-4">
                    <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-5 lg:p-6">
                        <h2 className="text-3xl lg:text-4xl font-bold text-center mb-4 text-blue-600">
                            {t('pricing.title')}
                        </h2>
                        <p className="text-lg lg:text-xl text-gray-600 text-center mb-8 lg:mb-12 max-w-3xl mx-auto">
                            {t('pricing.subtitle')}
                        </p>

                        {/* Pricing Cards */}
                        <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto mb-12">
                            {/* Base card styles - apply to all cards */}
                            {[
                                // Monthly Plan
                                {
                                    badge: t('welcome.pricing.monthlyPlan.badge', 'Try & Explore'),
                                    title: t('welcome.pricing.monthlyPlan.title', 'Monthly Plan'),
                                    price: t('welcome.pricing.monthlyPlan.price', '€4.99'),
                                    interval: t('welcome.pricing.monthlyPlan.interval', '/month'),
                                    description: <>
                                        <div className="text-sm font-medium">{t('welcome.pricing.monthlyPlan.description', 'Perfect for short-term planning')}</div>
                                    </>,
                                    features: [
                                        `✓ ${t('welcome.pricing.features.fullAccess')}`,
                                        `✓ ${t('welcome.pricing.features.freeTrial')}`,
                                        `✓ ${t('welcome.pricing.features.cancelAnytime')}`,
                                        `✓ ${t('welcome.pricing.features.regularUpdates')}`,
                                        `✓ ${t('welcome.pricing.features.emailSupport')}`
                                    ],
                                    buttonStyle: "bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white",
                                    isPopular: false
                                },
                                // Yearly Plan
                                {
                                    badge: t('welcome.pricing.annualPlan.badge', 'Best Value'),
                                    title: t('welcome.pricing.annualPlan.title', 'Annual Plan'),
                                    price: t('welcome.pricing.annualPlan.price', '€49'),
                                    interval: t('welcome.pricing.annualPlan.interval', '/year'),
                                    description: <>
                                        <div className="text-sm text-green-600 font-medium">{t('welcome.pricing.annualPlan.description', 'Save 18% vs Monthly')}</div>
                                    </>,
                                    features: [
                                        `✓ ${t('welcome.pricing.features.everythingInMonthly')}`,
                                        `✓ ${t('welcome.pricing.features.prioritySupport')}`,
                                        `✓ ${t('welcome.pricing.features.earlyAccessFeatures')}`,
                                        `✓ ${t('welcome.pricing.features.priceLockGuarantee')}`,
                                        `✓ ${t('welcome.pricing.features.annualSavings')}`
                                    ],
                                    buttonStyle: "bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white",
                                    isPopular: true
                                },
                                // Lifetime Plan
                                {
                                    badge: t('welcome.pricing.lifetimePlan.badge', 'Best Long-Term Value'),
                                    title: t('welcome.pricing.lifetimePlan.title', 'Lifetime Access'),
                                    price: remainingSpots > 0 ? t('welcome.pricing.lifetimePlan.price', '€99') : "€149",
                                    interval: t('welcome.pricing.lifetimePlan.interval', ''),
                                    description: remainingSpots > 0 ? (
                                        <>
                                            <p className="text-sm text-green-600 font-medium">{t('welcome.pricing.lifetimePlan.description', 'One-time payment, forever access')}</p>
                                        </>
                                    ) : (
                                        <p className="text-sm text-gray-600">{t('welcome.pricing.lifetimePlan.description', 'One-time payment, forever access')}</p>
                                    ),
                                    features: [
                                        `✓ ${t('welcome.pricing.features.unlimitedLifetime')}`,
                                        `✓ ${t('welcome.pricing.features.allFutureUpdates')}`,
                                        `✓ ${t('welcome.pricing.features.prioritySupportLifetime')}`,
                                        `✓ ${t('welcome.pricing.features.earlyAccessNewFeatures')}`,
                                        `✓ ${t('welcome.pricing.features.neverPayAgain')}`
                                    ],
                                    buttonStyle: "bg-gradient-to-r from-gray-900 to-gray-800 hover:from-gray-800 hover:to-gray-700 text-white",
                                    isPopular: false
                                }
                            ].map((plan, index) => (
                                <div 
                                    key={index}
                                    className={`bg-white rounded-xl p-6 border${plan.isPopular ? '-2 border-blue-600' : ' border-gray-200'} 
                                        shadow-md hover:shadow-xl transition-all duration-300 flex flex-col h-full relative 
                                        transform hover:-translate-y-1`}
                                >
                                    {/* Popular Badge */}
                                    {plan.isPopular && (
                                        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                                            <span className="bg-blue-600 text-white text-sm font-semibold px-4 py-1 rounded-full shadow-md">
                                                {t('welcome.pricing.mostPopular', 'Most Popular')}
                                            </span>
                                        </div>
                                    )}
                                    
                                    {/* Early Access Badge for Lifetime */}
                                    {index === 2 && remainingSpots > 0 && (
                                        <div className="absolute -top-4 right-4 transform hover:scale-105 transition-transform">
                                            <div className="relative">
                                                <span className="bg-gradient-to-r from-blue-600 via-blue-500 to-green-500 text-white text-sm font-semibold px-4 py-1.5 rounded-full shadow-lg flex items-center gap-2">
                                                    <svg className="w-4 h-4 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M10 2a1 1 0 00-1 1v1.323l-3.954 1.582a1 1 0 00-.646.934v4.286a1 1 0 00.646.934L9 13.641V17a1 1 0 102 0v-3.359l3.954-1.582a1 1 0 00.646-.934V6.839a1 1 0 00-.646-.934L12 4.323V3a1 1 0 00-1-1z" clipRule="evenodd"/>
                                                    </svg>
                                                    <span>{t('welcome.earlyAccessOffer')}</span>
                                                    <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">
                                                        {t('welcome.remainingSpots', { count: remainingSpots })}
                                                    </span>
                                                </span>
                                                <div className="absolute inset-0 bg-gradient-to-r from-blue-600 via-blue-500 to-green-500 opacity-30 blur-xl -z-10"></div>
                                            </div>
                                        </div>
                                    )}

                                    <div className="flex-grow">
                                        {/* Plan Badge */}
                                        <div className="text-sm font-medium text-blue-600 bg-blue-50 rounded-full px-3 py-1 inline-block mb-4">
                                            {plan.badge}
                                        </div>

                                        {/* Title */}
                                        <h3 className="text-xl font-bold text-gray-900 mb-2">{plan.title}</h3>

                                        {/* Pricing */}
                                        <div className="mb-4">
                                            <div className="flex items-baseline">
                                                <span className="text-4xl font-bold text-gray-900">{plan.price}</span>
                                                <span className="text-gray-600 ml-2">{plan.interval}</span>
                                                {index === 2 && remainingSpots > 0 && (
                                                    <span className="text-lg text-gray-500 line-through ml-2">€149</span>
                                                )}
                                            </div>
                                            <div className="mt-2">{plan.description}</div>
                                        </div>

                                        {/* Features */}
                                        <ul className="space-y-3 mb-6">
                                            {plan.features.map((feature, idx) => (
                                                <li key={idx} className="flex items-start gap-2 text-gray-600">
                                                    <span className="text-sm">{feature}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>

                                    {/* CTA Button */}
                                    <div className="mt-auto space-y-4">
                                        <Link to="/login" className={`block w-full ${plan.buttonStyle} font-semibold py-3 px-6 rounded-lg transition-all duration-300 shadow-md text-center`}>
                                            {t('welcome.pricing.startTrial', 'Start 3-Day Free Trial')}
                                        </Link>
                                        <p className="text-sm text-gray-500 text-center">{t('welcome.pricing.noCreditCard', 'No credit card required')}</p>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Features List */}
                        <div className="border-t border-gray-200 pt-6">
                            <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">
                                {t('welcome.everythingYouGet')}
                            </h3>
                            <div className="max-w-4xl mx-auto">
                                <ul className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 text-left">
                                    {pricingFeatures.map((feature, i) => (
                                        <li key={i} className="flex items-center text-gray-700">
                                            <svg className="w-5 h-5 text-blue-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                            </svg>
                                            <span className="text-sm">{feature}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>

                        {/* Creator Affiliate Ribbon */}
                        <div className="mt-8 mb-6">
                            <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-xl p-4 max-w-2xl mx-auto">
                                <div className="flex items-center justify-center gap-3">
                                    <div className="flex items-center gap-2">
                                        <span className="text-2xl">🎬</span>
                                        <div>
                                            <p className="text-sm font-semibold text-gray-900">{t('welcome.areYouCreator')}</p>
                                            <p className="text-sm text-gray-600">{t('welcome.earnReferral')}</p>
                                        </div>
                                    </div>
                                    <a 
                                        href="https://livelovespain.endorsely.com" 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
                                    >
                                        {t('welcome.learnMore')} →
                                    </a>
                                </div>
                            </div>
                        </div>

                        {/* Product Hunt Badge */}
                        <div className="mt-8 flex justify-center">
                            <a
                                href="https://www.producthunt.com/products/livelovespain?embed=true&utm_source=badge-featured&utm_medium=badge"
                                target="_blank"
                                rel="noopener noreferrer nofollow"
                                aria-label="Support LiveLoveSpain on Product Hunt"
                                className="inline-block hover:scale-105 transition-transform duration-200"
                            >
                                <img
                                    src="https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=1012408&theme=light&t=1756905289589"
                                    alt="LiveLoveSpain - Your all-in-one relocation and immigration app | Product Hunt"
                                    width="250"
                                    height="54"
                                    style={{ width: 250, height: 54 }}
                                    loading="lazy"
                                    decoding="async"
                                    referrerPolicy="no-referrer"
                                />
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* Top Guides Section */}
            <section id="top-guides-section" className="py-8 lg:py-12 bg-white scroll-mt-20">
                <div className="container mx-auto px-4">
                    <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-5 lg:p-6">
                    <h2 className="text-3xl lg:text-4xl font-bold text-center mb-3 text-blue-600">
                            {t('welcome.topGuides')}
                    </h2>
                        <p className="text-lg text-gray-700 mb-6 text-center max-w-3xl mx-auto">
                            {t('welcome.topGuidesSubtitle')}
                        </p>
                        
                        {/* Carousel Section */}
                        <div className="relative">
                            <div className="overflow-hidden">
                                <div 
                                    className="flex transition-transform duration-300 ease-in-out"
                                    style={{ transform: `translateX(-${currentSlide * 100}%)` }}
                                >
                                    {Array.from({ length: totalSlides }).map((_, slideIndex) => (
                                        <div key={slideIndex} className="w-full flex-shrink-0">
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                                {topGuides.slice(slideIndex * 3, (slideIndex + 1) * 3).map((article) => (
                                                    <Link
                                                        key={article.id}
                                                        to={createLocalizedUrl(`/guides/${getEnglishSlug(article)}`, currentLanguage)}
                                                        className="group relative bg-white rounded-2xl border border-gray-200 overflow-hidden hover:border-blue-200 hover:shadow-xl transition-all duration-300 block h-full flex flex-col"
                                                    >
                                                        {/* Image Container - Hidden on mobile */}
                                                        <div className="relative h-56 overflow-hidden hidden md:block">
                                                            {shouldLoadGuideImages ? (
                                                                <img 
                                                                    src={article.imageUrl}
                                                                    alt={article.title}
                                                                    className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-300"
                                                                    loading="lazy"
                                                                    decoding="async"
                                                                />
                                                            ) : (
                                                                <div className="w-full h-full bg-gray-200 animate-pulse flex items-center justify-center">
                                                                    <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                                    </svg>
                                                                </div>
                                                            )}
                                                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                                                            <div className="absolute top-4 left-4">
                                                                <span className="px-3 py-1 bg-blue-600/90 text-white text-xs font-medium rounded-full backdrop-blur-sm">
                                                                    {t(`guides.categories.${article.category}`, article.category)}
                                                                </span>
                                                            </div>
                                                        </div>

                                                        {/* Mobile Category Badge - Only visible on mobile */}
                                                        <div className="md:hidden p-4 pb-2">
                                                            <span className="px-3 py-1 bg-blue-600 text-white text-xs font-medium rounded-full">
                                                                {t(`guides.categories.${article.category}`, article.category)}
                                                            </span>
                                                        </div>

                                                        {/* Content Container */}
                                                        <div className="p-6 flex flex-col flex-grow">
                                                            <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors line-clamp-2">
                                                                {article.title}
                                                            </h3>
                                                            <p className="text-gray-600 mb-4 line-clamp-3 flex-grow">
                                                                {article.shortDescription}
                                                            </p>
                                                            <div className="flex items-center justify-between mt-auto pt-4 border-t border-gray-100">
                                                                <div className="flex items-center text-sm text-gray-500">
                                                                    <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                                    </svg>
                                                                    {new Date(article.content.lastUpdated || article.content.publishDate).toLocaleDateString(currentLanguage, {
                                                                        year: 'numeric',
                                                                        month: 'long',
                                                                        day: 'numeric'
                                                                    })}
                                                                </div>
                                                                <div className="flex items-center text-blue-600 font-medium group-hover:translate-x-1 transition-transform">
                                                                    <span className="text-sm">{t('welcome.readMore')}</span>
                                                                    <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                                                    </svg>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </Link>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Navigation Buttons */}
                            {currentSlide > 0 && (
                                <button
                                    onClick={() => setCurrentSlide(prev => Math.max(0, prev - 1))}
                                    className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 bg-white rounded-full p-2 shadow-lg hover:bg-gray-50 transition-colors"
                                    aria-label="Previous slide"
                                >
                                    <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                                    </svg>
                                </button>
                            )}
                            {currentSlide < totalSlides - 1 && (
                                <button
                                    onClick={() => setCurrentSlide(prev => Math.min(totalSlides - 1, prev + 1))}
                                    className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 bg-white rounded-full p-2 shadow-lg hover:bg-gray-50 transition-colors"
                                    aria-label="Next slide"
                                >
                                    <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                    </svg>
                                </button>
                            )}

                            {/* Dots Navigation */}
                            <div className="flex justify-center mt-6 space-x-2">
                                {Array.from({ length: totalSlides }).map((_, index) => (
                                    <button
                                        key={index}
                                        onClick={() => setCurrentSlide(index)}
                                        className={`w-2 h-2 rounded-full transition-colors ${
                                            index === currentSlide ? 'bg-blue-600' : 'bg-gray-300'
                                        }`}
                                        aria-label={`Go to slide ${index + 1}`}
                                        aria-current={index === currentSlide}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <section id="faq" className="py-8 lg:py-12 bg-white scroll-mt-20">
                <div className="container mx-auto px-4">
                    <h2 className="text-3xl lg:text-4xl font-bold text-center mb-3 text-blue-600">
                        {t('faq.title', 'Frequently Asked Questions')}
                    </h2>
                    <p className="text-lg text-gray-700 mb-6 text-center max-w-3xl mx-auto">
                        {t('welcome.faq.title')}
                    </p>
                    
                    {/* FAQ Container - Updated styling */}
                    <div className="max-w-3xl mx-auto bg-gray-50 rounded-2xl border border-gray-200 p-6 lg:p-8 shadow-lg">
                        {!faqConfig ? (
                            /* Loading state with skeleton */
                            <div className="animate-pulse space-y-4">
                                {Array.from({ length: 5 }).map((_, index) => (
                                    <div key={index} className="border-b border-gray-200 last:border-b-0 pb-4">
                                        <div className="flex justify-between items-center">
                                            <div className="h-5 bg-gray-200 rounded w-3/4"></div>
                                            <div className="h-5 w-5 bg-gray-200 rounded"></div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            currentFaqEntries.map((faq, index) => (
                                <div 
                                    key={index} 
                                    className={`border-b border-gray-200 last:border-b-0 ${
                                        index === 0 ? '' : 'mt-4'
                                    }`}
                                >
                                    <button
                                        className="w-full flex justify-between items-center py-4 text-left hover:text-blue-600 transition-colors"
                                        onClick={() => toggleFaq(index)}
                                        aria-expanded={openFaqIndex === index}
                                        aria-controls={`faq-answer-${index}`}
                                    >
                                        <h3 id={`faq-${index}`} className="text-lg font-semibold">{faq.question}</h3>
                                        <span className={`ml-4 transition-transform duration-200 ${
                                            openFaqIndex === index ? 'rotate-180' : ''
                                        }`}>
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </span>
                                    </button>
                                    {openFaqIndex === index && (
                                        <div 
                                            id={`faq-answer-${index}`}
                                            className="pb-4 text-gray-600 animate-fadeIn"
                                            aria-labelledby={`faq-${index}`}
                                        >
                                            {faq.answer}
                                        </div>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default Welcome;